import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LoginSocialApple } from 'reactjs-social-login';

import Modal from 'components/Modal';
import Button from 'components/Button';
import { DeleteOutlineIcon } from 'utils/icons';
import { useAccountSettingsContext } from 'layouts/AccountSettingsLayout/contexts/AccountSettingsContext';
import { useAppContext } from 'contexts/AppContext';
import { carryOutLogout } from 'utils/common';
import { toastStyle } from 'utils/helper';
import ALL_ROUTES from 'config/routes';
import API_ENDPOINTS from 'utils/api/endpoints';
import APP_CONFIG from 'config/app-config';

const DeleteAccountModal = ({ show, onHide }) => {
  const [deleteShared, setDeleteShared] = useState(true);
  const [accountInfo, setAccountInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [authorizingApple, setAuthorizingApple] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const { deleteAccountMutation } = useAccountSettingsContext();
  const { setIsUserLoggedIn } = useAppContext();
  const navigate = useNavigate();

  useEffect(() => {
    if (show) {
      setDeleteShared(true);
      setAccountInfo(null);
      setPreviewError(false);
      API_ENDPOINTS.account_settings
        .get_delete_account_info()
        .then(res => setAccountInfo(res?.data))
        .catch(() => setPreviewError(true));
    }
  }, [show]);

  const handleConfirm = async () => {
    if (
      !accountInfo ||
      accountInfo.requires_apple_authorization ||
      authorizingApple ||
      loading
    )
      return;
    setLoading(true);
    try {
      await deleteAccountMutation.mutateAsync({
        delete_shared_budgets: deleteShared,
      });
      toast.success('Your account has been deleted.', toastStyle);
      carryOutLogout(setIsUserLoggedIn);
      navigate(ALL_ROUTES.AUTH_CHILDREN.LOGIN);
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        'An error occurred while deleting your account. Please try again.';
      toast.error(message, toastStyle);
    } finally {
      setLoading(false);
    }
  };

  const confirmApple = async ({ data }) => {
    const code = data?.authorization?.code;
    if (!code) {
      toast.error(
        'Apple did not return a confirmation. Please try again.',
        toastStyle
      );
      return;
    }
    setAuthorizingApple(true);
    try {
      await API_ENDPOINTS.account_settings.authorize_apple_deletion({ code });
      const response =
        await API_ENDPOINTS.account_settings.get_delete_account_info();
      setAccountInfo(response?.data);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          error?.response?.data?.non_field_errors?.[0] ||
          'Use the Apple account linked to CineBudget Pro and try again.',
        toastStyle
      );
    } finally {
      setAuthorizingApple(false);
    }
  };

  const totalDeletions =
    (accountInfo?.non_shared_budgets_count || 0) +
    (deleteShared ? accountInfo?.shared_budgets_count || 0 : 0);

  return (
    <Modal
      show={show}
      onHide={onHide}
      className="delete-account-modal"
      size="md"
      centered={true}
    >
      <div className="delete-account-modal__body">
        <div className="delete-account-modal__icon">
          <DeleteOutlineIcon />
        </div>
        <h4 className="delete-account-modal__title">Delete Account</h4>
        <p className="delete-account-modal__warning">
          This action cannot be undone. Your account and all associated data
          will be permanently deleted.
        </p>

        {previewError && (
          <p role="alert">
            Account details could not be loaded. Close this window and try
            again.
          </p>
        )}
        {accountInfo?.requires_apple_authorization && (
          <div>
            <p>
              Confirm your linked Apple account so its authorization can be
              revoked when you delete your account.
            </p>
            <LoginSocialApple
              client_id={APP_CONFIG.socialLoginClientId.apple}
              scope="name email"
              redirect_uri={APP_CONFIG.redirectURI}
              onResolve={confirmApple}
              onReject={() =>
                toast.error(
                  'Apple confirmation was not completed. Your account has not been deleted.',
                  toastStyle
                )
              }
            >
              <Button
                label={
                  authorizingApple ? 'Confirming...' : 'Confirm Apple Account'
                }
                isDisabled={authorizingApple || loading}
                variant="grey-outlined"
              />
            </LoginSocialApple>
          </div>
        )}

        {accountInfo && (
          <div className="delete-account-modal__info">
            {totalDeletions > 0 && (
              <p className="delete-account-modal__budget-count">
                <strong>{totalDeletions}</strong> budget
                {totalDeletions !== 1 ? 's' : ''} will be permanently deleted.
              </p>
            )}

            {accountInfo.shared_budgets_count > 0 && (
              <div className="delete-account-modal__shared-options">
                <p className="delete-account-modal__shared-label">
                  You have <strong>{accountInfo.shared_budgets_count}</strong>{' '}
                  shared budget
                  {accountInfo.shared_budgets_count !== 1 ? 's' : ''}:
                </p>
                <label className="delete-account-modal__radio">
                  <input
                    type="radio"
                    name="deleteShared"
                    checked={deleteShared}
                    onChange={() => setDeleteShared(true)}
                  />
                  <span>Delete all my budgets including shared ones</span>
                </label>
                <label className="delete-account-modal__radio">
                  <input
                    type="radio"
                    name="deleteShared"
                    checked={!deleteShared}
                    onChange={() => setDeleteShared(false)}
                  />
                  <span>
                    Keep shared budgets for collaborators (ownership will be
                    transferred)
                  </span>
                </label>
              </div>
            )}
          </div>
        )}

        <div className="delete-account-modal__actions">
          <Button
            label="Cancel"
            onClick={onHide}
            variant="grey-outlined"
            className="delete-account-modal__cancel-btn"
            isDisabled={loading || authorizingApple}
          />
          <Button
            label={loading ? 'Deleting...' : 'Delete My Account'}
            onClick={handleConfirm}
            variant="danger"
            className="delete-account-modal__confirm-btn"
            isDisabled={
              loading ||
              authorizingApple ||
              !accountInfo ||
              accountInfo.requires_apple_authorization
            }
          />
        </div>
      </div>
    </Modal>
  );
};

DeleteAccountModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};

export default DeleteAccountModal;
